/* ============================================================================
 * i18nLabels.js — translate-at-render helpers for stored/data strings.
 *
 * These helpers map free-form strings stored in user metadata, seed data, or
 * domain enums (roles, phase names, virtual jobs, note types) to i18n keys so
 * everything displays in the active language without mutating the underlying
 * data.
 *
 * All helpers accept a `t` function (from `useTranslation()`) and a raw value
 * and return a localized label. If a value can't be cleanly mapped, the
 * helper falls back to the formatted raw string.
 * ========================================================================== */

const norm = (s) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[\s/_-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const titleCase = (s) =>
  String(s || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/* ----------------------------------------------------------------------------
 * Role labels — maps free-form role strings ("Owner / GC", "Lead Carpenter",
 * "general_contractor", "owner-gc", etc.) to brand.roles.<key>.
 * Falls back to title-cased raw value if no match.
 * ------------------------------------------------------------------------- */
const ROLE_KEY_MAP = {
  owner: 'owner',
  owner_gc: 'owner_gc',
  ownergc: 'owner_gc',
  general_contractor: 'general_contractor',
  gc: 'general_contractor',
  contractor: 'contractor',
  lead_carpenter: 'lead_carpenter',
  lead: 'lead_carpenter',
  carpenter: 'carpenter',
  foreman: 'foreman',
  operator: 'operator',
  helper: 'helper',
  apprentice: 'apprentice',
  crew: 'crew',
  crew_member: 'crew',
  team_member: 'team_member',
  team: 'team_member',
  admin: 'admin',
};

export const translateRole = (t, raw) => {
  if (raw == null || raw === '') return '';
  const key = ROLE_KEY_MAP[norm(raw)];
  if (key) {
    const translated = t(`brand.roles.${key}`, { defaultValue: '' });
    if (translated) return translated;
  }
  return titleCase(raw);
};

/* ----------------------------------------------------------------------------
 * Phase labels — maps phase keys (lead/demo/framing/.../complete) to
 * jobs.phase_labels.<key>. Returns the raw label if no translation exists.
 * ------------------------------------------------------------------------- */
const PHASE_KEY_MAP = {
  lead: 'lead',
  demo: 'demo',
  framing: 'framing',
  rough_ins: 'rough_ins',
  roughins: 'rough_ins',
  rough: 'rough_ins',
  drywall: 'drywall',
  paint: 'paint',
  trim: 'trim',
  punch_list: 'punch_list',
  punchlist: 'punch_list',
  complete: 'complete',
};

export const translatePhase = (t, raw) => {
  if (raw == null || raw === '') return '';
  const key = PHASE_KEY_MAP[norm(raw)];
  if (key) {
    const translated = t(`jobs.phase_labels.${key}`, { defaultValue: '' });
    if (translated) return translated;
  }
  return titleCase(raw);
};

/* ----------------------------------------------------------------------------
 * Note type labels — maps general/milestone/issue/client/field to
 * jobs.notes.types.<key>. Falls back to title-cased value.
 * ------------------------------------------------------------------------- */
export const translateNoteType = (t, raw) => {
  const k = norm(raw);
  if (!k) return t('jobs.notes.types.general');
  const allowed = ['general', 'milestone', 'issue', 'client', 'field'];
  if (allowed.includes(k)) {
    return t(`jobs.notes.types.${k}`);
  }
  return titleCase(raw);
};

/* ----------------------------------------------------------------------------
 * Virtual job description — translate SHOP/ADMIN helper text.
 * The data stays English (SHOP / ADMIN labels are deliberately uppercase
 * canonical codes); only the human-readable description is localized.
 * ------------------------------------------------------------------------- */
export const translateVirtualJobDesc = (t, id) => {
  if (id === '__SHOP__') return t('crewClock.virtual_shop_desc');
  if (id === '__ADMIN__') return t('crewClock.virtual_admin_desc');
  return '';
};
